import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { PaymentsErrorCodes } from '@app/translations';
import { FormActionTypeEnum, SEPAPaymentRailTypeEnum } from '@finxone-platform/form-action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  BaseWidgetProperties,
  OrganisationList,
  SystemRole,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { first, map } from 'rxjs';
import { GetBeneficiaryResponse } from '../../../services/account-service/account.type';
import { ConfigService } from '../../../services/config-service/config-service.service';
import { AccountState, PaymentGatewayConfig } from '../../../state/account.state';
import { BeneficiaryState } from '../../../state/beneficiary.state';
import { FormActionState } from '../../../state/form-submision.state';
import { ProfileState } from '../../../state/user-profile.state';
import { findAndShowAlertFromCode } from '../../alert-code-utils/alert-code.utils';
import { paymentValidationsForSEPA } from '@finxone-platform/shared/frontend';
import { isValidScheduleDate } from '../cta-actions/unified-payments/payment-utils';
import { redirectToPage } from '../cta-button-actions.utils';

/**
 * Validates the current page and checks the user's balance before redirecting
 * to the appropriate payment summary page or displaying an error alert.
 *
 * @param widgetProperties - The properties of the widget being processed.
 * @param injector - The injector used to retrieve necessary services.
 */
export function checkPaymentRequest(widgetProperties: BaseWidgetProperties, injector: Injector) {
  const store = injector.get(Store);
  const router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);
  const formSubmissionService = injector.get(FormSubmissionService);
  const configService = injector.get(ConfigService);
  checkBalanceAndRedirect(store, router, alertHandlerService);

  // check if schedule date is selected and it is valid
  isValidScheduleDate(injector);

  let activeUser: SystemRole;
  let activeUserType = '';
  const profile = store.selectSnapshot(ProfileState.getProfile);
  if (formSubmissionService.allPagesAreValid()) {
    configService
      .getRoles()
      .pipe(
        first(),
        map((roles) => {
          Object.entries(roles).forEach(([roleKey, value]) => {
            if (roleKey === profile.activeRole) {
              activeUser = value;
            }
          });
          if (activeUser?.userType) {
            activeUserType = activeUser.userType;
          }
        }),
      )
      .subscribe();
    //get system config from form state
    const paymentGatewayConfig = store.selectSnapshot(AccountState.getPaymentGatewayConfig);
    const activeOrg = profile.orgList?.find((org) => org.id === profile.activeOrganisationId);
    const parsedWidgetProperties = JSON.parse(JSON.stringify(widgetProperties));
    //if exists system config then redirect to payment approver/requestor flow
    if (paymentGatewayConfig?.paymentRequest?.enabled && activeUserType === 'org') {
      redirectToPaymentSummaryPage(parsedWidgetProperties, injector, activeOrg, paymentGatewayConfig);
    } else {
      redirectToPage(router, widgetProperties);
    }
  } else {
    alertHandlerService.showAlertFn('error', ' Please ensure that all the details are provided and correct.');
  }
}

/**
 * Redirects the user to the appropriate payment summary page based on their role.
 *
 * @param parsedWidgetProperties - The properties of the widget, including navigation details.
 * @param injector - The injector used to retrieve necessary services.
 * @param activeOrg - The active organization details, including user roles.
 * @param paymentGatewayConfig - Configuration details for the payment gateway.
 *
 * The function checks the user's roles within the active organization to determine
 * if they are a payment initiator, requestor, or approver. It redirects initiators
 * to the 'payment-summary-initiator' page and requestors or approvers to the
 * 'payment-summary-requestor' page. If the user lacks the necessary roles, an error
 * alert is displayed.
 */
function redirectToPaymentSummaryPage(
  parsedWidgetProperties: BaseWidgetProperties,
  injector: Injector,
  activeOrg: OrganisationList | undefined,
  paymentGatewayConfig: PaymentGatewayConfig,
) {
  const router = injector.get(Router);
  const alertHandlerService = injector.get(AlertHandlerService);
  const isPaymentInitiator = !!activeOrg?.roles.find((value) =>
    paymentGatewayConfig?.paymentRequest?.paymentInitiatorRoles.includes(value),
  );
  const isPaymentRequestor = !!activeOrg?.roles.find((value) =>
    paymentGatewayConfig?.paymentRequest?.paymentRequestorRoles.includes(value),
  );
  const isPaymentApprover = !!activeOrg?.roles.find((value) =>
    paymentGatewayConfig?.paymentRequest?.paymentApproverRoles.includes(value),
  );
  //Only initiator role have permission to make payment directly
  if (isPaymentInitiator) {
    parsedWidgetProperties['urlToNavigate'] = 'payment-summary-initiator';
    redirectToPage(router, parsedWidgetProperties, injector);
  } else if (isPaymentRequestor || isPaymentApprover) {
    parsedWidgetProperties['urlToNavigate'] = 'payment-summary-requestor';
    redirectToPage(router, parsedWidgetProperties, injector);
  } else {
    alertHandlerService.showAlertFn(
      'error',
      "You don't have permission to make a payment or to request for a payment",
    );
  }
}

/**
 * Checks the balance of the active account and redirects based on the transaction validity.
 *
 * This function retrieves form data and account information from the store, verifies if the
 * account has sufficient balance for the transaction, and handles alerts for insufficient
 * balance or invalid amounts. If the account currency is EUR, it proceeds to validate the
 * SEPA transaction.
 *
 * @param store - The application's state management store.
 * @param router - The router for navigation.
 * @param alertHandlerService - Service to handle alert notifications.
 *
 * @throws Will throw an error if form data or account data is undefined, if the account
 *         with the active ID is not found, if the balance or amount is NaN, or if the
 *         balance is insufficient.
 */
function checkBalanceAndRedirect(store: Store, router: Router, alertHandlerService: AlertHandlerService) {
  const formStateData = store.selectSnapshot(FormActionState.getFormActionState).response?.formData;
  const activeAccountId = store.selectSnapshot(AccountState.getCurrentAccountId);

  const accountsData = store.selectSnapshot(AccountState.getAccounts);

  if (formStateData?.['amount']) {
    if (!formStateData || !accountsData) {
      throw new Error('formData or accountsData is undefined or null');
    }

    const account = accountsData.accounts.find((account) => account.accountId === activeAccountId);
    if (!account) {
      throw new Error('Account with activeAccountId not found');
    }

    const amountPlusFees = Number(formStateData?.['totalAmount'] || formStateData?.['amount']);

    // Validate that the amount and account balance are valid numbers
    // For multi-currency accounts, find the balance for the current currency
    let accountBalance = Number(account.balance);
    if (account.accountType === 'multi-currency') {
      const multiCurrencyCode = store.selectSnapshot(AccountState.getCurrentMultiCurrencyCode);

      const balanceForCurrency = account.balance.find((b) => b.currency === multiCurrencyCode);
      if (balanceForCurrency) {
        accountBalance = Number(balanceForCurrency.amount);
      }
    }

    if (isNaN(accountBalance) || isNaN(amountPlusFees)) {
      throw new Error('Account balance or payment amount NaN');
    }

    const isBalanceAvailable = accountBalance >= amountPlusFees;

    if (!isBalanceAvailable) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [PaymentsErrorCodes.INSUFFICIENT_BALANCE]);
      throw new Error('Enter a lower amount to continue.');
    }

    /**
     * SEPA transaction validation only works with @default {EURO}
     */
    if (account?.currency === 'EUR') {
      return validateSEPATransaction(
        store,
        router,
        alertHandlerService,
        amountPlusFees,
        formStateData?.['bid'],
      );
    }
  } else {
    // Validate the transaction when amount is invalid.
    findAndShowAlertFromCode(store, router, alertHandlerService, [PaymentsErrorCodes.INVALID_AMOUNT]);
    throw new Error('Please enter a valid amount to proceed with the payment.');
  }
}

/**
 * Validate SEPA Transaction and handle validations for Target2 payment rail.
 * @param {Store} store - The application store instance.
 * @param {AlertHandlerService} alertHandlerService - Service to handle alert messages.
 * @param {number} amountPlusFees - Total amount including fees to validate.
 * @param {string} bid - Beneficiary ID for validation.
 * @throws Will throw an error if SEPA validations fail.
 */
const validateSEPATransaction = (
  store: Store,
  router: Router,
  alertHandlerService: AlertHandlerService,
  amountPlusFees: number,
  bid: string,
) => {
  // Get payment rail type from the store
  const paymentRail = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT),
  )?.formData?.paymentRail as SEPAPaymentRailTypeEnum;

  const toggleRailSelector = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormActionTypeEnum.SEPA_PAYMENT_RAIL_FLAG),
  )?.formData?.toggleRailSelector;

  // Fetch beneficiary details based on the provided bid
  const beneficiaryList = store.selectSnapshot(BeneficiaryState.getBeneficiary);
  const beneficiaryDetails = beneficiaryList?.items?.find((v) => v.id === bid);

  // Perform SEPA payment rail validations
  if (toggleRailSelector && paymentRail) {
    const paymentValidations = paymentValidationsForSEPA[paymentRail];

    // Validate minimum and maximum transaction limits
    if (amountPlusFees < 0) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [PaymentsErrorCodes.AMOUNT_TOO_LOW]);
      throw new Error(`The entered amount is below the minimum allowed limit.`);
    } else if (amountPlusFees > Number(paymentValidations?.maximumTransactionLimit)) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [
        PaymentsErrorCodes.EXCEEDED_TRANSFER_LIMIT,
      ]);
      throw new Error(`The transfer amount exceeds the allowed limit for ${paymentRail}.`);
    }

    // Validate additional requirements for the Target2 payment rail.
    return validateSEPATarget2PaymentRail(
      store,
      router,
      paymentRail,
      beneficiaryDetails,
      alertHandlerService,
    );
  }
};

/**
 * Validate additional requirements for the Target2 payment rail.
 * @param {SEPAPaymentRailTypeEnum} paymentRail - SEPA payment rail type.
 * @param {GetBeneficiaryResponse | undefined} beneficiaryDetails - Details of the beneficiary.
 * @param {AlertHandlerService} alertHandlerService - Service to handle alert messages.
 * @throws Will throw an error if Target2-specific validations fail.
 */
const validateSEPATarget2PaymentRail = (
  store: Store,
  router: Router,
  paymentRail: SEPAPaymentRailTypeEnum,
  beneficiaryDetails: GetBeneficiaryResponse | undefined,
  alertHandlerService: AlertHandlerService,
) => {
  if (paymentRail === SEPAPaymentRailTypeEnum.TARGET2) {
    // Validate BIC for Target2 payments
    if (!beneficiaryDetails?.destinationIdentifier?.bic) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [
        PaymentsErrorCodes.PAYMENT_FAILED_BIC_REQUIRED,
      ]);
      throw new Error(
        'Mandatory field missing: BIC is required for Target2 payments. Please complete the required fields and try again.',
      );
    }

    // Validate address details for Target2 payments
    if (!beneficiaryDetails?.address || Object.keys(beneficiaryDetails?.address).length === 0) {
      findAndShowAlertFromCode(store, router, alertHandlerService, [
        PaymentsErrorCodes.PAYMENT_FAILED_ADDRESS_REQUIRED,
      ]);
      throw new Error(
        'Mandatory field missing: Address is required for Target2 payments. Please complete the required fields and try again.',
      );
    }
  }
};
